import Calendar16 from '@carbon/icons-react/es/calendar/16';
import ChevronLeft16 from '@carbon/icons-react/es/chevron--left/16';
import ChevronRight16 from '@carbon/icons-react/es/chevron--right/16';
import { Popover } from 'antd';
import moment, { Moment } from 'moment';
import React, { FC, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Button } from 'components/atoms/button';
import { DatePicker } from 'components/atoms/date-picker';
import {
  endOfWeek,
  mondayOfWeek,
  weekFormatter,
} from 'components/organisms/timesheets/common/utils';

const Wrapper = styled.div`
  display: flex;
  align-items: center;
`;

const ThisWeekText = styled.div`
  font-weight: bold;
  padding-left: var(--spacing-04);
`;

const StyledButton = styled(Button)`
  padding-left: var(--spacing-03);
  margin-left: var(--spacing-02);
`;

interface DateRangeProps {
  onChange: (date: Moment) => void;
  value: Moment;
}

const renderWeekDate = (day: Moment, format = 'DD MMM YY'): string => {
  return `${mondayOfWeek(day).format(format)} - ${endOfWeek(day).format(
    format,
  )}`;
};

export const WeekRangePicker: FC<DateRangeProps> = ({
  onChange,
  value: selectedDay = mondayOfWeek(moment()),
}) => {
  const [datePickerVisibility, setDatePickerVisibility] = useState(false);

  const changeDatePickerVisibility = (visible: boolean): void => {
    setDatePickerVisibility(visible);
  };

  const toggleDatePickerVisibility = (): void => {
    changeDatePickerVisibility(!datePickerVisibility);
  };

  const nextWeek = () => {
    const nextWeekStartDay = mondayOfWeek(selectedDay.add(1, 'week'));

    onChange(nextWeekStartDay);
  };

  const previousWeek = () => {
    const previousWeekStartDay = mondayOfWeek(selectedDay.subtract(1, 'week'));

    onChange(previousWeekStartDay);
  };

  return (
    <Wrapper>
      <Button
        type="text"
        size="small"
        icon={<ChevronLeft16 />}
        onClick={previousWeek}
      />
      <Button
        type="text"
        size="small"
        icon={<ChevronRight16 />}
        onClick={nextWeek}
      />
      {selectedDay.isSame(mondayOfWeek(), 'day') && (
        <ThisWeekText>
          <FormattedMessage id="this_week" />
          <span>:</span>
        </ThisWeekText>
      )}

      <Popover
        id="date-range-selector"
        content={
          <DatePicker
            open
            allowClear={false}
            picker="week"
            value={selectedDay}
            onChange={(selectedDate) => {
              changeDatePickerVisibility(false);
              const selectedWeekStartDay = mondayOfWeek(selectedDate!);

              onChange(selectedWeekStartDay);
            }}
            format={weekFormatter}
            getPopupContainer={(): HTMLElement =>
              document.getElementById('date-range-selector') as HTMLElement
            }
          />
        }
        placement="bottomLeft"
        trigger="click"
        overlayClassName="due__date__picker"
        visible={datePickerVisibility}
        onVisibleChange={setDatePickerVisibility}
      >
        <StyledButton
          type="text"
          size="small"
          icon={<Calendar16 />}
          onClick={toggleDatePickerVisibility}
          data-cy-button="week-range-picker-trigger"
        >
          <span>{selectedDay != null ? renderWeekDate(selectedDay) : ''}</span>
        </StyledButton>
      </Popover>
    </Wrapper>
  );
};
